import { useMemo, useRef } from "react"

import { findAncestors } from "shared/lib/treeUtils"
import {
  FloorTypename,
  ProductPositionTypename,
  Project,
  ProjectChild,
  RoomTypename,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"

import { useProjectChildFragment } from "entities/project-children/api/useProjectChildFragment"
import { useProjectData } from "entities/projects/api/useProjectData"
import { useProjectPageContext } from "entities/projects/model/useProjectPageContext"
import { useProjectStore } from "features/project-management/model/useProjectStore"

export interface UseDefaultProjectChildPositionArgs {
  projectID: string
  childType: Exclude<ProjectChild["__typename"], undefined>
}

export interface UseDefaultProjectChildPositionResult {
  defaultParentID: string
  defaultPosition: number
  isLoadingDefaultPosition: boolean
}

export function useDefaultProjectChildPosition({
  projectID,
  childType
}: UseDefaultProjectChildPositionArgs): UseDefaultProjectChildPositionResult {
  const { selectedElementIDs, activeFloorID } = useProjectStore(projectID)
  const { project, isLoading } = useProjectData(projectID, true)
  const { isCurrentPage } = useProjectPageContext(projectID)

  const selectedItem = useProjectChildFragment(
    selectedElementIDs[0] || activeFloorID
  )

  const computedDefaults = useMemo(() => {
    if (!selectedItem || !project || !isCurrentPage)
      return { defaultParentID: projectID, defaultPosition: 0 }

    const ancestors = findAncestors<Project | ProjectChild | null>(
      selectedItem?.id,
      project?.children,
      true
    )

    if (childType === FloorTypename) {
      const referencePosition =
        project.children
          ?.filter(isProjectFloor)
          .findIndex(child => child.id === selectedItem.id) ?? -1

      return {
        defaultParentID: projectID,
        defaultPosition: referencePosition !== -1 ? referencePosition + 1 : 0
      }
    }

    if (childType === RoomTypename) {
      const parent =
        ancestors?.find(ancestor => isProjectFloor(ancestor)) || project

      const room = ancestors?.find(ancestor => isProjectRoom(ancestor))

      const referencePosition =
        parent.children
          ?.filter(child => isProjectRoom(child))
          .findIndex(child => child.id === room?.id) ?? -1

      return {
        defaultParentID: parent.id,
        defaultPosition: referencePosition !== -1 ? referencePosition + 1 : 0
      }
    }

    if (childType === ProductPositionTypename) {
      const parent =
        ancestors?.find(ancestor => isProjectRoom(ancestor)) || project

      const productPosition =
        ancestors?.find(ancestor => isProjectProductPosition(ancestor)) ||
        project

      const referencePosition =
        parent.children
          ?.filter(child => isProjectProductPosition(child))
          .findIndex(child => child.id === productPosition.id) ?? -1

      return {
        defaultParentID: parent.id,
        defaultPosition: referencePosition !== -1 ? referencePosition + 1 : 0
      }
    }

    return {
      defaultParentID: projectID,
      defaultPosition: 0
    }
  }, [
    childType,
    projectID,
    selectedItem,
    selectedElementIDs,
    project,
    isCurrentPage
  ])

  // Creating a child selects it optimistically while the overlay is still
  // open, which would shift the default to "new child + 1" mid-submit. Lock
  // the defaults in once the project has loaded so they reflect the selection
  // at the time the overlay was opened.
  const lockedDefaultsRef = useRef<typeof computedDefaults | null>(null)
  if (!lockedDefaultsRef.current && !isLoading) {
    lockedDefaultsRef.current = computedDefaults
  }
  const { defaultParentID, defaultPosition } =
    lockedDefaultsRef.current ?? computedDefaults

  return {
    defaultParentID,
    defaultPosition,
    isLoadingDefaultPosition: isLoading
  }
}
